/**
 * The live vehicle marker: a top-down bus drawn as inline SVG.
 *
 * Inline rather than a PNG on purpose — beyond staying crisp at any zoom it
 * removes a bug class outright. The previous marker was fetched by URL and
 * 404'd in production when the path ignored basePath (fixed in d8c00a9);
 * an inline SVG has no URL to get wrong.
 *
 * Two structural rules, both enforced by tests:
 *   - the shadow filter is applied ABOVE the rotation, so the light source
 *     stays fixed while the vehicle turns;
 *   - the age chip lives OUTSIDE the rotating group, so it never renders
 *     upside-down when the bus heads south.
 */

export const MARKER_SIZE_PX = 36;
export const STALE_OPACITY = 0.55;

const SVG_NS = 'http://www.w3.org/2000/svg';

// Filter ids must be unique per marker; duplicated ids across markers make the
// browser resolve every reference to the first one.
let markerSeq = 0;

const svgEl = <K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Record<string, string>,
): SVGElementTagNameMap[K] => {
  const node = document.createElementNS(SVG_NS, name);
  Object.entries(attrs).forEach(([k, v]) => node.setAttribute(k, v));
  return node;
};

// An age counts UP, so both branches floor: rounding made 90s read "2m ago",
// claiming the fix is older than it is. Flooring the seconds branch too keeps
// the two consistent — rounding there would send 59.5s into the minutes
// branch, where flooring renders it "0m ago".
export function formatAge(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  return `${Math.floor(ms / 60000)}m ago`;
}

export function createBusMarkerElement(heading: number): HTMLDivElement {
  markerSeq += 1;
  const filterId = `bus-shadow-${markerSeq}`;

  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'display:flex;flex-direction:column;align-items:center;opacity:1;';

  const svg = svgEl('svg', {
    width: String(MARKER_SIZE_PX),
    height: String(MARKER_SIZE_PX),
    viewBox: '0 0 40 40',
    'aria-hidden': 'true',
  });

  const defs = svgEl('defs', {});
  const filter = svgEl('filter', {
    id: filterId, x: '-70%', y: '-70%', width: '240%', height: '240%',
  });
  filter.appendChild(svgEl('feDropShadow', {
    dx: '0',
    dy: '2.2',
    stdDeviation: '1.7',
    'flood-color': '#201e21',
    'flood-opacity': '0.45',
  }));
  defs.appendChild(filter);
  svg.appendChild(defs);

  // Filter on the OUTER group: its dx/dy are in unrotated space, so the
  // shadow offset stays put while the inner group spins.
  const shadowed = svgEl('g', { filter: `url(#${filterId})` });
  const rotor = svgEl('g', {
    'data-bus-rotate': '',
    transform: `rotate(${heading} 20 20)`,
  });

  rotor.appendChild(svgEl('rect', {
    x: '11.5',
    y: '5',
    width: '17',
    height: '30',
    rx: '5.5',
    fill: '#ffe200',
    stroke: '#201e21',
    'stroke-width': '2.1',
  }));
  rotor.appendChild(svgEl('path', {
    d: 'M13.6 10.2 Q20 7.4 26.4 10.2 L26.4 12.4 L13.6 12.4 Z',
    fill: '#201e21',
  }));
  [17.5, 22.5].forEach((y) => {
    rotor.appendChild(svgEl('rect', {
      x: '14.4',
      y: String(y),
      width: '11.2',
      height: '1.7',
      rx: '0.85',
      fill: '#201e21',
      opacity: '0.28',
    }));
  });
  rotor.appendChild(svgEl('rect', {
    x: '14.4',
    y: '30',
    width: '11.2',
    height: '1.8',
    rx: '0.9',
    fill: '#201e21',
    opacity: '0.5',
  }));

  shadowed.appendChild(rotor);
  svg.appendChild(shadowed);
  wrapper.appendChild(svg);

  const chip = document.createElement('div');
  chip.setAttribute('data-bus-age', '');
  chip.style.cssText = [
    'display:none', 'margin-top:2px', 'padding:2px 6px', 'border-radius:9999px',
    'background:rgba(255,255,255,0.95)', 'color:#6b5f4d', 'font-size:10px',
    'font-weight:700', 'white-space:nowrap',
    'box-shadow:0 2px 6px rgba(0,0,0,0.05)',
  ].join(';');
  wrapper.appendChild(chip);

  return wrapper;
}

export function setBusMarkerHeading(el: HTMLDivElement, heading: number): void {
  el.querySelector('[data-bus-rotate]')
    ?.setAttribute('transform', `rotate(${heading} 20 20)`);
}

/**
 * Show or hide the age chip.
 *
 * The stale FADE is deliberately not applied here. MapLibre's Marker owns
 * `element.style.opacity`: `Marker._update()` is bound to the map's `move`
 * event and also runs on every `setLngLat()`, and it ends by writing the
 * marker's internal `_opacity` back onto the element. An opacity written
 * straight onto the element therefore survives only until the next pan. The
 * fade goes through `marker.setOpacity()` in gmap.tsx instead.
 */
export function setBusMarkerStale(el: HTMLDivElement, staleAgeMs?: number): void {
  const chip = el.querySelector('[data-bus-age]') as HTMLElement | null;
  if (!chip) return;
  if (staleAgeMs === undefined) {
    chip.style.display = 'none';
    return;
  }
  chip.style.display = 'block';
  chip.textContent = `Updated ${formatAge(staleAgeMs)}`;
}
